// src/modules/requerimiento/views/adicion/Programacion.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EquiposAInstalar from "../../components/adicion/EquiposAInstalar";
import AsignarEquipoDialog from "../../components/adicion/AsignarEquipoDialog";
import VinculacionAdicionalSection from "./VinculacionAdicionalSection";
import { useAsignacionesModems } from "../../hooks/useAsignacionesModems";
import { useCatalogoModems } from "../../hooks/useCatalogoModems";
import { useCatalogoModemsVendidos } from "../../hooks/useCatalogoModemsVendidos";
import {
  assignChip as assignChipService,
  getAdicionAsignados,
  getAdicionPlacasModemsVinculacion,
  postVincularPlacaClienteAdicional,
  updateRequerimiento,
} from "../../services/reqSolicitudes";

export default function AdicionProgramacion({
  cod,
  sucursal,
  fmtFecha,
  fmtHora,
  estadoMap,
  refreshKey,
  onToast,
  tercia,
  onRefreshRequerimiento,
  codServicioCliente,
  codCliente,
  tipoAdicion,
  clienteNombre = "",
  servicioDescripcion = "",
  onEditarCoordinacion,
  // Dentro de Trabajo de campo (adición sin Programación): sin botón "Siguiente", y al asignar
  // un equipo un requerimiento que seguía en 1/2 pasa solo a Trabajo de campo.
  embebido = false,
  estado,
}) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const tipoAdicionNorm = String(tipoAdicion || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  const esVinculacionAdicional = tipoAdicionNorm === "VINCULACION_ADICIONAL";

  const { items: equipos, total: totalEquipos, loading: equiposLoading, refetch: refetchCatalogo } =
    useCatalogoModems({ cod, sucursal, q, page, limit, enabled: !esVinculacionAdicional });

  const [pageVendidos, setPageVendidos] = useState(1);
  const { items: equiposVendidos, total: totalEquiposVendidos, loading: equiposVendidosLoading, refetch: refetchCatalogoVendidos } =
    useCatalogoModemsVendidos({
      cod, codCliente, codServicioCliente, q, page: pageVendidos, limit,
      enabled: !esVinculacionAdicional,
    });

  const { items: asignados, loading: asignadosLoading, refetch: refetchAsignados } =
    useAsignacionesModems(cod);

  // Vinculación adicional: los equipos ya vinculados salen del endpoint de unidades asignadas (trae placa, IMEI, modelo y chips).
  const [vinculados, setVinculados] = useState([]);
  const [vinculadosLoading, setVinculadosLoading] = useState(false);
  const fetchVinculados = useCallback(async () => {
    if (!esVinculacionAdicional || !cod) { setVinculados([]); return; }
    try {
      setVinculadosLoading(true);
      const { data } = await getAdicionAsignados(cod);
      setVinculados(Array.isArray(data) ? data : []);
    } catch (e) {
      setVinculados([]);
    } finally {
      setVinculadosLoading(false);
    }
  }, [cod, esVinculacionAdicional]);
  useEffect(() => { fetchVinculados(); }, [fetchVinculados]);

  const [placasModems, setPlacasModems] = useState([]);
  const [placasReq, setPlacasReq] = useState([]); // placas registradas en el requerimiento (solo se ofrecen los equipos de esas placas)
  const [sinPlacasReq, setSinPlacasReq] = useState(false);
  const [placasLoading, setPlacasLoading] = useState(false);
  const [vinculando, setVinculando] = useState("");

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalEquipos / limit)), [totalEquipos, limit]);
  const totalPagesVendidos = useMemo(() => Math.max(1, Math.ceil(totalEquiposVendidos / limit)), [totalEquiposVendidos, limit]);

  useEffect(() => { setPage(1); setPageVendidos(1); }, [q, limit]);

  const fetchPlacasModems = useCallback(async () => {
    if (!esVinculacionAdicional || !cod) {
      setPlacasModems([]);
      return;
    }

    try {
      setPlacasLoading(true);
      const { data: resp } = await getAdicionPlacasModemsVinculacion(cod, { buscar: q, limit: Math.max(limit, 50) });
      const items = Array.isArray(resp)
        ? resp
        : Array.isArray(resp?.data)
          ? resp.data
          : Array.isArray(resp?.data?.items)
            ? resp.data.items
            : Array.isArray(resp?.items)
              ? resp.items
              : [];
      setPlacasModems(items);
      const meta = resp?.items ? resp : resp?.data?.items ? resp.data : {};
      setPlacasReq(Array.isArray(meta.placas_requerimiento) ? meta.placas_requerimiento : []);
      setSinPlacasReq(!!meta.sin_placas);
    } catch (e) {
      setPlacasModems([]);
      setPlacasReq([]);
      setSinPlacasReq(false);
      onToast?.({ sev: "error", msg: e?.response?.data?.message || e?.message || "No se pudo cargar placas y modems" });
    } finally {
      setPlacasLoading(false);
    }
  }, [cod, esVinculacionAdicional, limit, onToast, q]);

  useEffect(() => { fetchPlacasModems(); }, [fetchPlacasModems]);

  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (esVinculacionAdicional) { fetchPlacasModems(); fetchVinculados(); }
    else { refetchCatalogo(); refetchCatalogoVendidos(); }
    refetchAsignados();
  }, [refreshKey, refetchCatalogo, refetchCatalogoVendidos, refetchAsignados, fetchPlacasModems, fetchVinculados, esVinculacionAdicional]);

  const [modalOpen, setModalOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [modemTarget, setModemTarget] = useState(null);

  // Un solo diálogo de 2 pasos: abrirlo muestra la búsqueda; elegir un equipo pasa a configurarlo
  // y "Atrás" vuelve a la búsqueda (modemTarget = null).
  const handleAbrirBusqueda = useCallback(() => { setModemTarget(null); setModalOpen(true); }, []);
  const handleOpenChip = useCallback((row) => { setModemTarget(row); setModalOpen(true); }, []);
  const handleVolverBusqueda = useCallback(() => { if (!assigning) setModemTarget(null); }, [assigning]);
  const handleCloseChip = useCallback(() => { if (!assigning) { setModalOpen(false); setModemTarget(null); } }, [assigning]);

  const handleAssignChip = useCallback(async (payload) => {
    if (!modemTarget?.num_sim) return;
    try {
      setAssigning(true);

      const extras = {
        cod_req: cod,
        modem_id: modemTarget.id ?? modemTarget.ID_A ?? null,
        num_sim: modemTarget.num_sim ?? null,
        cod_servicio_cliente: codServicioCliente ?? null,
        cod_cliente: codCliente ?? null,
        tercia: tercia ?? null,
      };

      if (payload?.bandera) {
        await assignChipService(cod, modemTarget.num_sim, { bandera: true, sensores: payload?.sensores ?? {}, ...extras });
        onToast?.({ sev: "success", msg: "Bandera confirmada" });
      } else if (payload?.sensor) {
        await assignChipService(cod, modemTarget.num_sim, { sensor: true, sensores: payload?.sensores ?? {}, ...extras });
        onToast?.({ sev: "success", msg: "Sensor confirmado" });
      } else {
        // Uno o varios chips (doble SIM); `numero` se manda igual con el primero.
        const numeros = Array.isArray(payload?.numeros) && payload.numeros.length
          ? payload.numeros
          : [payload?.numero ?? payload?.chip?.numero ?? ""].filter(Boolean);
        const numero = numeros[0] ?? "";
        const origen_id = payload?.origen_id ?? (typeof payload?.origen === "object" ? payload?.origen?.id : payload?.origen) ?? null;
        const origen_nombre = payload?.origen_nombre ?? (typeof payload?.origen === "object" ? payload?.origen?.nombre : null) ?? null;

        if (!numero) throw new Error("Falta el numero de chip.");

        await assignChipService(cod, modemTarget.num_sim, { numero, numeros, origen_id, origen_nombre, sensores: payload?.sensores ?? {}, ...extras });
        onToast?.({ sev: "success", msg: numeros.length > 1 ? `Chips ${numeros.join(", ")} asignados` : `Chip ${numero} asignado` });
      }

      if (embebido && [1, 2].includes(Number(estado))) {
        // Si falla no se avisa: el equipo ya quedó asignado y la vista es la misma.
        await updateRequerimiento(cod, { estado: 3 }).catch(() => {});
      }
      await Promise.all([refetchCatalogo(), refetchCatalogoVendidos(), refetchAsignados()]);
      await onRefreshRequerimiento?.();
      setModalOpen(false);
      setModemTarget(null);
    } catch (e) {
      onToast?.({ sev: "error", msg: e?.response?.data?.message || e?.message || "No se pudo asignar el chip" });
    } finally {
      setAssigning(false);
    }
  }, [cod, modemTarget, codServicioCliente, codCliente, tercia, embebido, estado,
      refetchCatalogo, refetchCatalogoVendidos, refetchAsignados, onToast, onRefreshRequerimiento]);

  const handleVincularPlaca = useCallback(async (placa) => {
    const placaValue = placa?.placa;
    if (!placaValue) return;

    try {
      setVinculando(placaValue);
      const { data: result } = await postVincularPlacaClienteAdicional(cod, placaValue, {
        cod_servicio_cliente: codServicioCliente,
        tipo_vinculacion: "Secundario",
      });
      const total = result?.total_modems || placa?.modems?.length || 0;
      onToast?.({ sev: "success", msg: `Placa ${placaValue} vinculada (${total} modem(s))` });
      await Promise.all([fetchPlacasModems(), fetchVinculados(), refetchAsignados()]);
      await onRefreshRequerimiento?.();
    } catch (e) {
      onToast?.({ sev: "error", msg: e?.response?.data?.message || e?.message || "No se pudo vincular la placa" });
    } finally {
      setVinculando("");
    }
  }, [cod, codServicioCliente, fetchPlacasModems, fetchVinculados, onRefreshRequerimiento, onToast, refetchAsignados]);

  const asignadosVista = esVinculacionAdicional ? vinculados : asignados;

  const [savingNext, setSavingNext] = useState(false);

  const handleNext = useCallback(async () => {
    try {
      setSavingNext(true);
      await updateRequerimiento(cod, { estado: 3 });
      await onRefreshRequerimiento?.();
      onToast?.({ sev: "success", msg: "Trabajo de Campo" });
    } catch (e) {
      onToast?.({ sev: "error", msg: e?.response?.data?.message || e?.message || "No se pudo actualizar el estado" });
    } finally {
      setSavingNext(false);
    }
  }, [cod, onToast, onRefreshRequerimiento]);

  return (
    <Box className="mt-3">
      {esVinculacionAdicional ? (
        <VinculacionAdicionalSection
          q={q}
          setQ={setQ}
          limit={limit}
          setLimit={setLimit}
          placas={placasModems}
          placasRequerimiento={placasReq}
          sinPlacasRequerimiento={sinPlacasReq}
          loading={placasLoading}
          onRefresh={fetchPlacasModems}
          onVincularPlaca={handleVincularPlaca}
          vinculando={vinculando}
          asignados={vinculados}
          asignadosLoading={vinculadosLoading}
          clienteNombre={clienteNombre}
          servicioDescripcion={servicioDescripcion}
          onEditarCoordinacion={onEditarCoordinacion}
        />
      ) : (
        <>
          <EquiposAInstalar total={asignados?.length ?? 0} loading={asignadosLoading} onBuscar={handleAbrirBusqueda} />
          <AsignarEquipoDialog
            open={modalOpen}
            onClose={handleCloseChip}
            q={q} setQ={setQ}
            page={page} setPage={setPage}
            totalPages={totalPages}
            totalEquipos={totalEquipos}
            equipos={equipos}
            equiposLoading={equiposLoading}
            pageVendidos={pageVendidos} setPageVendidos={setPageVendidos}
            totalPagesVendidos={totalPagesVendidos}
            totalEquiposVendidos={totalEquiposVendidos}
            equiposVendidos={equiposVendidos}
            equiposVendidosLoading={equiposVendidosLoading}
            codCliente={codCliente}
            sucursal={sucursal}
            modemTarget={modemTarget}
            onElegir={handleOpenChip}
            onVolver={handleVolverBusqueda}
            assigning={assigning}
            onAssign={handleAssignChip}
          />
        </>
      )}

      {!embebido && !modalOpen && (asignadosVista?.length ?? 0) > 0 && (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Tooltip title="Avanzar a Trabajo de Campo">
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={savingNext}
                startIcon={savingNext ? null : <ArrowForwardIcon />}
              >
                {savingNext ? <><CircularProgress size={18} sx={{ mr: 1, color: "inherit" }} />Guardando...</> : "Siguiente"}
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}
